import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useEffectiveClientId } from '@/hooks/useAuth'
import type { SeedBalance, SeedAction, SeedLedgerEntry } from '@/types/database'

/**
 * Seed costs, mirrored from the rate card.
 *
 * These exist so a button can show its price before it is tapped. They are NOT
 * the source of truth: the database charges the real amount inside
 * spend_seeds(), and a mismatch here changes the label, never the charge.
 */
export const SEED_COSTS = {
  // Brian's COST (app-data.js) and DEVELOPER-HANDOFF §3, Oct 22 2026.
  build_story: 12,
  reroll: 3,          // a reroll, or Pull 5 more
  regenerate: 3,      // a rewrite
  voice_build: 1,     // another position
  add_region: 5,      // add or swap a place, once
  reset_mix: 5,
  pill_research: 1,
  newsletter_version: 1,
  breaking_alert: 1,  // from the alert budget, not the week
  reformat: 4,
  // Adding and removing topics is free; a reset costs.
  swap_topic: 0,
} as const satisfies Partial<Record<SeedAction, number>>

export type PricedAction = keyof typeof SEED_COSTS

/**
 * Emma (Brian, emma-chat.js): her suggestions are free; anything typed is one
 * of the week's questions, 5 included on every plan, then 1 seed each until
 * Monday. The server's emma_charge() decides; these only label the sheet.
 */
export const EMMA_FREE_PER_WEEK = 5
export const EMMA_MESSAGE_COST = 1

/**
 * "What uses seeds", in the words the seed panel uses (Brian's PRICES). The
 * seed_costs table on the server holds the same list.
 */
export const PRICE_LIST: { label: string; seeds: number | 'Included'; note?: string }[] = [
  { label: 'A story', seeds: SEED_COSTS.build_story },
  { label: 'Another position (Newcomer, Reporter or Insider)', seeds: SEED_COSTS.voice_build },
  { label: 'A rewrite', seeds: SEED_COSTS.regenerate },
  { label: 'A reroll, or Pull 5 more', seeds: SEED_COSTS.reroll },
  { label: 'The newsletter version of a story', seeds: SEED_COSTS.newsletter_version },
  { label: 'Checking a keyword’s trend', seeds: SEED_COSTS.pill_research },
  { label: 'Add or swap a place, once', seeds: SEED_COSTS.add_region },
  { label: 'A breaking alert', seeds: SEED_COSTS.breaking_alert, note: 'from the alert budget' },
  { label: 'Emma', seeds: 'Included', note: `${EMMA_FREE_PER_WEEK} questions a week included` },
]

/** The Recent list's words for each ledger action (Brian's ACT in topbar.js). */
export const LEDGER_LABELS: Partial<Record<SeedAction, string>> = {
  build_story: 'Built a story',
  reroll: 'Pull 5 more',
  regenerate: 'Regenerated',
  reformat: 'Reformatted',
  voice_build: 'Another position',
  add_region: 'Added a place',
  reset_mix: 'Reset the mix',
  pill_research: 'Keyword trend',
  newsletter_version: 'Newsletter version',
  breaking_alert: 'Breaking alert',
  alert_budget: 'Alert budget',
  advisor_question: 'Asked Emma',
  advisor_topup: 'Filled Emma’s silo',
  swap_topic: 'Swapped a topic',
  borrow: 'Borrowed a story',
}

export function useSeedBalance() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['seed-balance', clientId],
    enabled: !!clientId,
    // The balance changes on every spend, so never serve it stale.
    staleTime: 0,
    queryFn: async (): Promise<SeedBalance | null> => {
      const { data, error } = await supabase.rpc('seed_balance', { p_client_id: clientId! })
      if (error) throw error
      return (data as SeedBalance[])?.[0] ?? null
    },
  })
}

/** Recent ledger activity, for the rate card / history view. */
export function useSeedHistory(limit = 50) {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['seed-history', clientId, limit],
    enabled: !!clientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('seed_ledger')
        .select('*')
        .eq('client_id', clientId!)
        .order('created_at', { ascending: false })
        .limit(limit)
      if (error) throw error
      return data as SeedLedgerEntry[]
    },
  })
}

export class InsufficientSeedsError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'InsufficientSeedsError'
  }
}

/**
 * Spend seeds for an action.
 *
 * Always pass an idempotencyKey derived from the thing being paid for (the
 * headline id, the story id). A retried request then charges once rather than
 * twice, which matters most on the swipe deck where a flaky connection can
 * easily fire the same build twice.
 */
export function useSpendSeeds() {
  const clientId = useEffectiveClientId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (args: {
      action: PricedAction
      amount?: number
      refType?: string
      refId?: string
      idempotencyKey?: string
    }) => {
      const amount = args.amount ?? SEED_COSTS[args.action]
      const { data, error } = await supabase.rpc('spend_seeds', {
        p_client_id: clientId!,
        p_amount: amount,
        p_action: args.action,
        p_ref_type: args.refType ?? null,
        p_ref_id: args.refId ?? null,
        p_idem_key: args.idempotencyKey ?? null,
      })
      if (error) {
        if (/insufficient seeds/i.test(error.message)) {
          throw new InsufficientSeedsError(error.message)
        }
        throw error
      }
      return (data as { spent: number; weekly_remaining: number; purchased_remaining: number }[])[0]
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seed-balance', clientId] })
      queryClient.invalidateQueries({ queryKey: ['seed-history', clientId] })
    },
  })
}

/** True when the client can afford an action. Used to disable a button rather
 *  than let the spend fail. */
export function useCanAfford(action: PricedAction): boolean {
  const { data } = useSeedBalance()
  if (!data) return false
  return data.total_available >= SEED_COSTS[action]
}

/**
 * The soft wall (Brian's topbar.js, app-data.js GG.borrow): once a week, when
 * this week's seeds can't cover a story, one story's seeds come from next week.
 */
export function useBorrowStory() {
  const clientId = useEffectiveClientId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('seed_borrow' as never)
      if (error) throw error
      return data as number
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seed-balance', clientId] })
      queryClient.invalidateQueries({ queryKey: ['seed-history', clientId] })
    },
  })
}

/**
 * Moves purchased seeds into Emma's silo, up to its capacity. Returns how many
 * moved. Silo seeds buy Emma and nothing else.
 */
export function useFillSilo() {
  const clientId = useEffectiveClientId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('fill_silo', {
        p_idem_key: `silo:${clientId}:${Math.floor(Date.now() / 1000)}`,
      })
      if (error) throw error
      return data as number
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seed-balance', clientId] })
      queryClient.invalidateQueries({ queryKey: ['seed-history', clientId] })
      queryClient.invalidateQueries({ queryKey: ['coin-case'] })
    },
  })
}
